import type { Agent } from '@studio/models/agent';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { createWithEqualityFn as create } from 'zustand/traditional';
import { shallow } from 'zustand/shallow';

import { config } from '#~/config/index.js';
import { shouldAutoLink } from '#~/routes/verify-auto-link/v2/auto-link-vendor.js';
import { isElectronApp } from './utils.js';

/**
 * ENG-4154 — renderer-side single source of truth for session identity.
 *
 * Replaces the per-consumer `getBridgeLinkedEncoderID()` poll+cache fan-out
 * (~15 independent callers, each with its own pRetry/30s cache) with ONE
 * store fed by a main→renderer push (`bridge:identity-changed`) plus a
 * centralized bounded pull fallback (`bridge:get-current-identity`). Push and
 * pull race; the first terminal state wins.
 *
 * Per W1 surface map §5 (event-loss resync) + knowledge
 * electron-auth-bootstrap-race (ai.knowledge/platform): on Windows
 * `webContents.send` is fire-and-forget and drops when the renderer listener
 * is not yet registered, so a push-only design still hangs. The pull fallback
 * + replay primitive (`pull()` reads the resolver's CURRENT state) closes that
 * gap.
 *
 * Per rule: code-comments-required / reference-rules-in-comments
 * (ai.knowledge/global) — comments explain WHY and name the rules/knowledge.
 */

/** Terminal-or-transitional identity status. */
export type IdentityStatus = 'resolving' | 'linked' | 'unlinked' | 'error';

/**
 * Structured error cause (W1 surface map §6). NEVER collapse to an opaque
 * single value — each cause drives a different UX/diagnostic.
 */
export type IdentityErrorCause = 'timeout' | 'sidecar_error' | 'ipc_lost' | 'readiness_ambiguous';

/** Source of a state transition, for the structured transition log. */
export type IdentityUpdateSource = 'push' | 'pull' | 'reset' | 'init' | 'prime';

export interface IdentityState {
  status: IdentityStatus;
  /** Linked agent id; present only when status === 'linked'. */
  agentId?: Agent['id'];
  /** Org id the linked agent belongs to, when main supplies it. */
  org?: T_Maybe<string>;
  /** Only set when status === 'error'. */
  cause?: IdentityErrorCause;
}

const RESOLVING: IdentityState = { status: 'resolving' };

const isTerminal = (s: IdentityStatus): boolean =>
  s === 'linked' || s === 'unlinked' || s === 'error';

/**
 * Hard terminals end the session's pull loop. `unlinked` is deliberately NOT
 * hard: on a fresh-install boot the sidecar genuinely answers "not linked"
 * while the auto-link flow is still in flight, and the pre-migration hook kept
 * polling through that window (CI regression, run 27298045549: latching
 * `loaded=true, agentId=undefined` early let the boot flow commit to a STALE
 * registry room — state sync started for the wrong agent and never recovered).
 * The loop keeps pulling on `unlinked` until `linked` lands or the session
 * ceiling settles it.
 */
const isHardTerminal = (s: IdentityStatus): boolean => s === 'linked' || s === 'error';

/**
 * Transport seam so the store is unit-testable without a live Electron IPC
 * bridge. Defaults bind to `ElectronAPI.getCurrentIdentityState()` (pull) and
 * the `bridge:identity-changed` IPC channel (push). Tests inject fakes via
 * {@link __configureIdentityForTest}.
 */
export interface IdentityTransport {
  /** Replay/pull primitive — reads the resolver's CURRENT IdentityState. */
  pull(): Promise<IdentityState>;
  /** Subscribe to pushed IdentityState updates; returns an unsubscribe fn. */
  subscribe(onPush: (state: IdentityState) => void): () => void;
  /** Monotonic clock (ms). Injectable so the ceiling is testable. */
  now(): number;
  /** Bounded pull cadence delay; injectable so tests don't wall-clock wait. */
  delay(ms: number): Promise<void>;
}

// --- Tunables (configurable per Phase 1 "bounded readiness wait"). --- //

/** Renderer-side last-resort ceiling. Keyed to the session (W1 §2). */
const DEFAULT_CEILING_MS = 30_000;
/** Bounded pull cadence — how often the fallback re-pulls while resolving. */
const PULL_INTERVAL_MS = 500;

interface IdentityStoreShape {
  identity: IdentityState;
  /** auth0 `sub` the current identity is keyed to (W1 §2). */
  sessionKey: T_Maybe<string>;
  /**
   * True once consumers may ACT on `identity` (the old `linkedLoaded`
   * contract). `linked`/`error` and authoritative primes settle immediately;
   * an early `unlinked` does NOT — it only settles when the session ceiling
   * expires. This restores the pre-migration semantics where the reactive hook
   * kept `loaded=false` through the auto-link window (≤30s) instead of letting
   * the boot flow branch on a not-yet-final "no linked agent" answer.
   */
  settled: boolean;
  /** Replace identity, logging the transition. Internal — prefer the helpers. */
  apply: (next: IdentityState, source: IdentityUpdateSource) => void;
  /** Settle without changing identity (ceiling expiry while `unlinked`). */
  markSettled: () => void;
}

/**
 * Emit a structured transition log on EVERY change (W1 §6). Per
 * code-comments-required: concise, one structured record. Keeps `cause` only
 * on `to: error`.
 */
const logTransition = (
  from: IdentityState,
  to: IdentityState,
  sessionKey: T_Maybe<string>,
  source: IdentityUpdateSource
): void => {
  console.info('[identity] transition', {
    from: from.status,
    to: to.status,
    sessionKey: sessionKey ?? null,
    source,
    ...(to.status === 'error' && to.cause ? { cause: to.cause } : {})
  });
};

// Gate #3 — Hive unlinked-bootstrap contract (ENG-4368). Whether Auto Link can
// run for THIS session's vendor. When it cannot (Hive / unknown / missing
// vendor), a genuine `unlinked` answer is authoritative the instant the sidecar
// delivers it — there is no auto-link happy path that would flip it to `linked`
// within the ceiling, so holding the full iris patience window is a pure ~30s
// boot delay on every Hive launch (contract symptom: "30s boot delay"). Iris
// keeps the patience window (auto-link may still populate the token late).
// Defaults to `true` (iris patience) so an unknown/unset vendor never settles
// early by accident — the store recomputes it from the single vendor decision
// in `ensureOrchestration`. Per rule: reference-rules-in-comments
// (ai.knowledge/global). See knowledge: hive-no-auto-link-bootstrap-contract.
let autoLinkVendorEligible = true;
/** Test seam override for {@link autoLinkVendorEligible}; undefined = compute from config. */
let autoLinkVendorEligibleForced: boolean | undefined;

export const useIdentityStore = create<IdentityStoreShape>()((set, get) => ({
  identity: RESOLVING,
  sessionKey: undefined,
  settled: false,
  apply: (next, source) => {
    const { identity, sessionKey, settled } = get();

    // Once terminal for this session, ignore late `resolving` echoes (a slow
    // pull resolving after a push already settled). A genuine change of the
    // linked id still applies. An explicit `reset` (account switch/logout) is
    // ALLOWED to drive terminal → resolving — that is the teardown path.
    if (source !== 'reset' && isTerminal(identity.status) && next.status === 'resolving') {
      return;
    }

    // Settle rules: hard terminals always settle; `prime` is the auto-link
    // flow's authoritative answer (settles even on unlinked); `init` is the
    // deterministic non-Electron path. A pull/push `unlinked` does NOT settle
    // for an auto-link-eligible (iris) vendor — the session ceiling owns that
    // (see isHardTerminal rationale above), because auto-link may still flip it
    // to `linked` within the window.
    //
    // Gate #3 (ENG-4368): for a vendor that never auto-links, a REAL `unlinked`
    // answer (from a pull/push — NOT a `resolving` transient) settles
    // immediately, so Hive boots to its pair screen at once instead of waiting
    // out the ~30s iris patience ceiling. Routed through the single vendor
    // decision via `autoLinkVendorEligible`. Per rule: reference-rules-in-comments.
    const settlesUnlinkedForNonAutoLinkVendor =
      next.status === 'unlinked' &&
      (source === 'pull' || source === 'push') &&
      !autoLinkVendorEligible;
    const settles =
      next.status === 'linked' ||
      next.status === 'error' ||
      source === 'prime' ||
      source === 'init' ||
      settlesUnlinkedForNonAutoLinkVendor;
    const nextSettled = source === 'reset' ? false : settled || settles;

    // No-op if nothing observable changed — avoids log spam and redundant
    // re-renders from repeated identical pulls.
    if (
      identity.status === next.status &&
      identity.agentId === next.agentId &&
      identity.org === next.org &&
      identity.cause === next.cause &&
      settled === nextSettled
    ) {
      return;
    }

    logTransition(identity, next, sessionKey, source);
    set({ identity: next, settled: nextSettled });
  },
  markSettled: () => {
    if (get().settled) {
      return;
    }
    // Structured observability for the ceiling-settle path (W1 §6).
    console.info('[identity] settled at ceiling', { status: get().identity.status });
    set({ settled: true });
  }
}));

// --- Module-scoped orchestration state (NOT per-mount, NOT a render value). --- //
//
// The ceiling + orchestration live here, keyed to the session. A React remount
// must NOT reset the ceiling (W1 §2 / Phase 3) — module scope + sessionKey
// guard guarantee that; a NEW session key resets cleanly via reset().

let activeTransport: IdentityTransport | null = null;
let unsubscribePush: (() => void) | null = null;
/** True once orchestration (push subscribe + pull loop) is running this session. */
let orchestrationStarted = false;
/** In-flight pull-fallback loop for the current session (test-awaitable). */
let pullFallbackPromise: Promise<void> | null = null;
/**
 * Orchestration generation. Bumped on every (re)start and reset so a stale
 * pull-fallback loop from a torn-down session bails instead of writing into
 * the store of a new session (account switch / test isolation).
 */
let orchestrationGen = 0;
/** Ceiling start for the current session; remount-stable. */
let ceilingStartedAt: number | null = null;
/** Whether any pull/push has been observed (distinguishes timeout vs ipc_lost). */
let observedAnyResponse = false;
let ceilingMs = DEFAULT_CEILING_MS;

const realTransport = (): IdentityTransport => ({
  pull: async () => {
    // Imported lazily to avoid a static import cycle (api.ts imports many
    // renderer modules). The resolver returns the full IdentityState.
    const { ElectronAPI } = await import('./api.js');
    return ElectronAPI.getCurrentIdentityState();
  },
  subscribe: (onPush) => {
    const handler = (_ev: unknown, state: IdentityState) => onPush(state);
    window.electron?.ipcRenderer?.on('bridge:identity-changed', handler);
    return () => {
      window.electron?.ipcRenderer?.removeListener?.('bridge:identity-changed', handler);
    };
  },
  now: () => Date.now(),
  delay: (ms) => new Promise((r) => setTimeout(r, ms))
});

const transport = (): IdentityTransport => activeTransport ?? realTransport();

/**
 * Bounded pull fallback. Re-pulls the resolver's current state on a bounded
 * cadence until a HARD terminal (`linked`/`error`) arrives, the store settles
 * (prime), or the session-keyed ceiling fires. A pulled `unlinked` is applied
 * (status is visible) but the loop KEEPS pulling — a late link (auto-link
 * completing, sidecar finishing warm-restart rehydration) must still flip the
 * store within the ceiling. Push (`bridge:identity-changed`) races this loop;
 * whichever delivers first wins (the store's `apply` ignores stale follow-ups).
 */
const runPullFallback = async (gen: number): Promise<void> => {
  const t = transport();

  // Loop until hard-terminal/settled or ceiling. The ceiling is checked
  // against the session-stable `ceilingStartedAt` so a remount mid-loop cannot
  // extend it. `gen` guards against a torn-down session's loop writing into a
  // new one.
  // eslint-disable-next-line no-constant-condition
  while (true) {
    if (gen !== orchestrationGen) {
      return;
    }
    {
      const s = useIdentityStore.getState();
      if (isHardTerminal(s.identity.status) || s.settled) {
        return;
      }
    }

    try {
      const pulled = await t.pull();
      if (gen !== orchestrationGen) {
        return; // session torn down while the pull was in flight.
      }
      observedAnyResponse = true;
      useIdentityStore.getState().apply(pulled, 'pull');
      if (isHardTerminal(pulled.status)) {
        return;
      }
    } catch (error) {
      // Transient IPC failure — keep trying until the ceiling. Do not swallow.
      console.warn('[identity] pull fallback attempt failed', error);
    }

    if (gen !== orchestrationGen) {
      return;
    }
    const after = useIdentityStore.getState();
    if (isHardTerminal(after.identity.status) || after.settled) {
      return;
    }

    // Ceiling check. Outcomes (W1 §6, revised per CI run 27303953778):
    //  - holding a genuine `unlinked` → settle WITHOUT error (the sidecar
    //    answered; nothing linked within the window — the boot flow may now
    //    branch to the not-linked path, mirroring the old hook's 30s cap)
    //  - still `resolving` but responses WERE observed (IPC alive, resolver
    //    alive, the sidecar just never produced an answer — e.g. a link held
    //    in flight) → ALSO settle `unlinked` without error. This is the old
    //    hook's 30s cap contract; turning it into a hard `error` painted the
    //    boot error UI over a healthy-but-slow link (mac bridge-offline e2e).
    //  - still `resolving` with ZERO responses → error/ipc_lost (push dropped
    //    AND pull dead — the one genuinely broken transport case).
    const startedAt = ceilingStartedAt ?? t.now();
    if (t.now() - startedAt >= ceilingMs) {
      if (after.identity.status === 'unlinked') {
        after.markSettled();
      } else if (observedAnyResponse) {
        useIdentityStore.getState().apply({ status: 'unlinked' }, 'pull');
        useIdentityStore.getState().markSettled();
      } else {
        useIdentityStore.getState().apply({ status: 'error', cause: 'ipc_lost' }, 'pull');
      }
      return;
    }

    await t.delay(PULL_INTERVAL_MS);
  }
};

/**
 * Idempotently start identity orchestration for the current session: subscribe
 * to push updates and kick the bounded pull fallback. Safe to call from many
 * consumers/mounts — the `orchestrationStarted` guard ensures the resolver
 * does NOT re-fan-out on remount (W1 Phase 3 invariant).
 */
const ensureOrchestration = (): void => {
  if (orchestrationStarted) {
    return;
  }
  orchestrationStarted = true;

  // Non-Electron (web) has no bridge: identity is deterministically unlinked
  // and immediately "loaded" so the bootstrap path never waits on a bridge.
  if (!isElectronApp()) {
    useIdentityStore.getState().apply({ status: 'unlinked' }, 'init');
    return;
  }

  // Gate #3 (ENG-4368): compute whether Auto Link can run for this vendor from
  // the single fail-closed decision. A non-eligible vendor (Hive / unknown)
  // lets a genuine `unlinked` settle immediately (see the `apply` settle rule);
  // iris keeps the patience window. The test seam can force this value.
  autoLinkVendorEligible =
    autoLinkVendorEligibleForced ?? shouldAutoLink({ isElectron: true, vendor: config?.vendor });

  const t = transport();
  if (ceilingStartedAt === null) {
    ceilingStartedAt = t.now();
  }

  unsubscribePush = t.subscribe((state) => {
    observedAnyResponse = true;
    useIdentityStore.getState().apply(state, 'push');
  });

  const gen = ++orchestrationGen;
  pullFallbackPromise = runPullFallback(gen);
};

/**
 * ENG-4154 PHASE D — self-start orchestration on ANY boot-protected route.
 *
 * `initIdentity` (the session-key binder) is only called by `useRootBootstrap`,
 * which mounts on the index route alone. A refresh that lands on a non-index
 * boot route (e.g. `/room/:id/boot`) therefore never started the resolver, so
 * the store latched `resolving` and the BootOverlay never reached `ready` — the
 * post-migration refresh regression (`studio-refresh.spec.ts`). Pre-migration
 * the reactive `useLinkedAgentID` hook ran its OWN poll, which self-started on
 * every route; this idempotent entry point restores that property WITHOUT
 * touching the session key (so account-switch reset semantics stay owned by
 * `initIdentity`). Safe to call from many mounts — `ensureOrchestration`'s
 * `orchestrationStarted` guard makes repeat calls a no-op.
 *
 * Per rule: reference-rules-in-comments (ai.knowledge/global). See knowledge:
 * electron-auth-bootstrap-race (ai.knowledge/platform) — the bounded pull
 * fallback must run for the loader to reach a terminal state on every route.
 */
export const ensureIdentityOrchestration = (): void => {
  ensureOrchestration();
};

/**
 * Bind the identity to a session key (auth0 `sub`, W1 §2) and start
 * orchestration. Called by `useRootBootstrap` once it knows the user.
 *
 * - Same key as the current session → no-op for the ceiling and NO re-fan-out
 *   (remount-safe).
 * - Binding a key onto orchestration that self-started keyless (current
 *   `undefined`, e.g. ENG-4154 PHASE D's `ensureIdentityOrchestration` kicked
 *   it from a non-index route first) → adopt the key, NO reset. Wiping the
 *   ceiling/identity here would discard an in-flight or already-terminal
 *   resolve and re-run the bounded window for no reason.
 * - Any transition AWAY from a previously-bound key — account switch (A→B) OR
 *   logout (A→undefined) → full reset (new ceiling, status back to `resolving`),
 *   killing the null-org corruption / stale-account identity from the prior
 *   session. This is the W2 teardown invariant and MUST be preserved.
 */
export const initIdentity = (sessionKey: T_Maybe<string>): void => {
  const current = useIdentityStore.getState().sessionKey;
  if (orchestrationStarted && current === sessionKey) {
    return; // remount with same session — do nothing (ceiling preserved).
  }

  // Reset on any move away from an already-bound key: account switch (A→B) or
  // logout (A→undefined). Only a first-time bind onto self-started orchestration
  // (current `undefined`) skips the reset — that path adopts the key below.
  if (orchestrationStarted && current !== undefined) {
    resetSessionIdentity(sessionKey);
    return;
  }

  if (orchestrationStarted) {
    // current === undefined: adopt the key onto already-running (keyless)
    // orchestration without resetting (the PHASE D self-start bind).
    useIdentityStore.setState({ sessionKey });
    return;
  }

  useIdentityStore.setState({ sessionKey });
  ensureOrchestration();
};

/**
 * Phase 4 — account-switch / logout teardown. Clears identity, org, the
 * session key, and the session-keyed ceiling, then forces status back to
 * `resolving` and restarts orchestration for the (possibly new) session.
 * One call site owns the reset so the renderer store, org, and linked-room
 * path can never be left holding a previous account's identity (null-org fix).
 */
export const resetSessionIdentity = (nextSessionKey?: T_Maybe<string>): void => {
  unsubscribePush?.();
  unsubscribePush = null;
  orchestrationStarted = false;
  ceilingStartedAt = null;
  observedAnyResponse = false;
  orchestrationGen++; // cancel any in-flight pull loop from the prior session.

  const store = useIdentityStore.getState();
  useIdentityStore.setState({ sessionKey: nextSessionKey });
  store.apply(RESOLVING, 'reset');

  ensureOrchestration();
};

/** Imperative "I just linked id X" entry point (was `primeBridgeLinkedEncoderID`). */
export const primeLinkedAgentId = (agentId: T_Maybe<Agent['id']>): void => {
  const normalized = agentId ? (agentId.toLowerCase() as Agent['id']) : undefined;
  useIdentityStore
    .getState()
    .apply(
      normalized ? { status: 'linked', agentId: normalized } : { status: 'unlinked' },
      'prime'
    );
};

/** Sync snapshot of the linked agent id (undefined unless status === 'linked'). */
export const getLinkedAgentId = (): T_Maybe<Agent['id']> => {
  const { identity } = useIdentityStore.getState();
  return identity.status === 'linked' ? identity.agentId : undefined;
};

/**
 * Store-backed replacement for `ElectronAPI.getBridgeLinkedEncoderID()`.
 *
 * `force` (decision-point callers: auto-link controller, linking screen)
 * mirrors the old contract — a LIVE sidecar consult whose first real terminal
 * answer is returned immediately, including a genuine `unlinked` (the
 * controller must start auto-link promptly on a fresh install, not wait out
 * the settle ceiling). The main resolver only ever reports `unlinked` from an
 * actual sidecar reply (never from silence), so that answer is trustworthy.
 *
 * Non-force (passive readers: proxy, livekit, discovery, watchdog) awaits the
 * SETTLED store — `linked`/`error`/prime or the ceiling — restoring the old
 * "patient" semantics where an in-flight auto-link window never yields a
 * premature `undefined`.
 */
export const resolveLinkedAgentId = async (force = false): Promise<T_Maybe<Agent['id']>> => {
  ensureOrchestration();

  if (force && isElectronApp()) {
    // Old `force` contract (the pre-migration pRetry): consult the sidecar
    // LIVE and RETRY until it produces a REAL terminal answer — never accept
    // the ceiling-fabricated `unlinked`. With a slow auth sidecar (e.g. the
    // 30s injected AUTH delay, run 27306118999 delay-amplification), the
    // session ceiling settles `unlinked` before the sidecar can answer; a
    // decision-point caller acting on that sends the auto-link controller
    // into a re-link on a LINKED machine → `autolink-error`. Bounded by the
    // ceiling from THIS call's start; callers (controller ipcTimeoutMs)
    // bound harder. A push-delivered hard terminal short-circuits the loop.
    const t = transport();
    const gen = orchestrationGen;
    const deadline = t.now() + ceilingMs;
    while (gen === orchestrationGen && t.now() < deadline) {
      try {
        const pulled = await t.pull();
        if (gen !== orchestrationGen) {
          break;
        }
        observedAnyResponse = true;
        useIdentityStore.getState().apply(pulled, 'pull');
        if (isTerminal(pulled.status)) {
          // Real terminal reply — return it directly (the store apply above
          // may legitimately no-op, e.g. identical repeat pulls).
          return pulled.status === 'linked' ? pulled.agentId : undefined;
        }
      } catch (error) {
        console.warn('[identity] forced pull failed', error);
      }
      const s = useIdentityStore.getState().identity;
      if (isHardTerminal(s.status)) {
        return s.status === 'linked' ? s.agentId : undefined;
      }
      await t.delay(PULL_INTERVAL_MS * 2);
    }
    // Deadline/teardown — fall back to the snapshot (undefined when the
    // sidecar never answered), mirroring the old pRetry-exhausted return.
    return getLinkedAgentId();
  }

  // Passive readers wait for the SETTLED store — linked/error/prime or the
  // session ceiling — so an in-flight auto-link window never yields a
  // premature `undefined`.
  const ready = (s: Pick<IdentityStoreShape, 'identity' | 'settled'>): boolean => s.settled;

  // If already answerable, return immediately.
  if (ready(useIdentityStore.getState())) {
    return getLinkedAgentId();
  }

  // Otherwise wait for the pull fallback / push / prime to get there.
  await new Promise<void>((resolve) => {
    const unsub = useIdentityStore.subscribe((s) => {
      if (ready(s)) {
        unsub();
        resolve();
      }
    });
    // Re-check after subscribing — a transition between the snapshot above and
    // the subscription would otherwise be missed forever.
    if (ready(useIdentityStore.getState())) {
      unsub();
      resolve();
    }
  });

  return getLinkedAgentId();
};

/**
 * Post-link handshake confirmation: wait (bounded) for the sidecar to report
 * `linked`, polling THROUGH genuine `unlinked` answers.
 *
 * This is the inverse of the `force` contract above. A cloud-side
 * AgentRegistryLink commits before the LOCAL sidecar completes its own
 * handshake (the launcher's link-request poll → mailroom callback → credential
 * write), so during that window the sidecar's honest answer is still
 * `unlinked`. A caller verifying a link it JUST made must treat that answer as
 * transient and keep consulting until `linked` lands, a hard `error` arrives,
 * or the deadline expires — accepting the first genuine `unlinked` fails
 * verification for a link that actually succeeded (QA repro 2026-07-28,
 * `studio_link_failed` / `error_type=bridge_handshake` while the Bridge showed
 * linked on the next boot).
 *
 * Returns the linked agent id, or undefined on sidecar error / deadline /
 * session teardown. The caller owns the identity comparison.
 */
export const waitForLinkedAgentId = async (
  timeoutMs: number = ceilingMs
): Promise<T_Maybe<Agent['id']>> => {
  ensureOrchestration();

  if (!isElectronApp()) {
    return getLinkedAgentId();
  }

  const t = transport();
  const gen = orchestrationGen;
  const deadline = t.now() + timeoutMs;
  while (gen === orchestrationGen && t.now() < deadline) {
    try {
      const pulled = await t.pull();
      if (gen !== orchestrationGen) {
        break;
      }
      observedAnyResponse = true;
      useIdentityStore.getState().apply(pulled, 'pull');
      if (pulled.status === 'linked') {
        return pulled.agentId;
      }
      if (pulled.status === 'error') {
        // Hard terminal — the sidecar's store read is degraded; more polling
        // cannot confirm the handshake. Surface the failure to the caller.
        return undefined;
      }
      // `unlinked` / `resolving` are TRANSIENT in this contract (unlike
      // `force`): the handshake may simply not have completed yet.
    } catch (error) {
      console.warn('[identity] link-confirmation pull failed', error);
    }
    // The concurrent orchestration loop (or a push) may land a hard terminal
    // between our pulls — honor the store snapshot before sleeping.
    const s = useIdentityStore.getState().identity;
    if (isHardTerminal(s.status)) {
      return s.status === 'linked' ? s.agentId : undefined;
    }
    await t.delay(PULL_INTERVAL_MS * 2);
  }
  return getLinkedAgentId();
};

// --- React selectors. --- //

/** Subscribe to the full IdentityState (for terminal-state bootstrap mapping). */
export const useIdentityState = (): IdentityState => useIdentityStore((s) => s.identity);

/**
 * Thin selector replacement for the old polling `useLinkedAgentID`. Returns
 * `[agentId, loaded]` where `loaded` maps to the store's `settled` flag —
 * `linked`/`error`/prime settle immediately; an early `unlinked` only settles
 * at the session ceiling. This is the old hook's contract (`loaded` flipped on
 * a real id or the 30s cap, never on a transient empty), which keeps the boot
 * flow from committing to the not-linked path while auto-link or sidecar
 * warm-up is still in flight (CI regression, run 27298045549).
 */
export const useLinkedAgentIDSelector = (): [T_Maybe<Agent['id']>, boolean] =>
  useIdentityStore(
    (s) => [s.identity.status === 'linked' ? s.identity.agentId : undefined, s.settled],
    shallow
  );

/** Result of a LIVE sidecar link consult (bypasses every session cache). */
export type SidecarLiveLinkState =
  | { readonly state: 'linked'; readonly agentId: Agent['id'] }
  | { readonly state: 'unlinked' }
  | { readonly state: 'unknown' };

/**
 * Consult the sidecar's CURRENT link state directly over the legacy
 * `bridge:get-id` IPC — bypassing BOTH sticky layers (this store and the
 * main-process resolver latch `linked` for the session; ipc.ts
 * `ensureMainIdentityResolved` keeps a linked resolve in-flight forever).
 *
 * Exists for decision points that must detect a link the sidecar has JUST
 * invalidated: the sidecar's boot validation serves the stale triple and
 * wipes it milliseconds later (CI run 27332628785, win delay-amplification
 * CV scenario — the auto-link controller skipped on the pre-wipe answer and
 * boot wedged in `skipped`). A cached `linked` cannot be re-trusted in that
 * window; only a live round-trip to `handleIDRequest` can.
 *
 * Reply mapping mirrors the main resolver's attemptGetId contract
 * (ipc.ts): a non-empty id string is `linked`; an EMPTY/absent id is a real
 * sidecar "not linked" answer; the handler's `'error'` sentinel (its catch
 * path) and transport failures are `unknown` — callers must treat `unknown`
 * as "no confirmation", never as a terminal unlinked.
 */
export const consultSidecarLinkLive = async (): Promise<SidecarLiveLinkState> => {
  if (!isElectronApp()) {
    return { state: 'unlinked' };
  }
  try {
    const reply: unknown = await window.electron?.ipcRenderer?.invoke('bridge:get-id');
    if (typeof reply === 'string' && reply !== '' && reply !== 'error') {
      return { state: 'linked', agentId: reply.toLowerCase() as Agent['id'] };
    }
    if (reply === '' || reply === undefined || reply === null) {
      return { state: 'unlinked' };
    }
    return { state: 'unknown' };
  } catch (error) {
    console.warn('[identity] live sidecar consult failed', error);
    return { state: 'unknown' };
  }
};

// --- Test seam. --- //

/** Configure the transport + ceiling for unit tests. */
export const __configureIdentityForTest = (
  overrides: Partial<IdentityTransport> & {
    ceilingMs?: number;
    /** Gate #3 seam — force the vendor's Auto Link eligibility (see apply). */
    autoLinkVendorEligible?: boolean;
  }
): void => {
  const base = realTransport();
  activeTransport = {
    pull: overrides.pull ?? base.pull,
    subscribe: overrides.subscribe ?? base.subscribe,
    now: overrides.now ?? base.now,
    delay: overrides.delay ?? base.delay
  };
  if (overrides.ceilingMs !== undefined) {
    ceilingMs = overrides.ceilingMs;
  }
  if (overrides.autoLinkVendorEligible !== undefined) {
    autoLinkVendorEligibleForced = overrides.autoLinkVendorEligible;
  }
};

/** Reset ALL module + store state between tests. */
export const __resetIdentityForTest = (): void => {
  unsubscribePush?.();
  unsubscribePush = null;
  activeTransport = null;
  orchestrationStarted = false;
  pullFallbackPromise = null;
  orchestrationGen++; // cancel any in-flight pull loop.
  ceilingStartedAt = null;
  observedAnyResponse = false;
  ceilingMs = DEFAULT_CEILING_MS;
  autoLinkVendorEligible = true;
  autoLinkVendorEligibleForced = undefined;
  useIdentityStore.setState({ identity: RESOLVING, sessionKey: undefined, settled: false });
};

/** Await the in-flight orchestration (pull fallback loop) for the session. */
export const __awaitOrchestration = (): Promise<void> => pullFallbackPromise ?? Promise.resolve();
